/* This example requires Tailwind CSS v2.0+ */
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, ExclamationIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { Button } from 'ui/components/Button'
import { useTranslation } from 'react-i18next'
import { Text } from 'ui/components/Text'
import { useAnalytics } from 'utilities/analytics'

export interface ModalProps {
  title?: string
  children?: ReactNode
  isOpen: boolean
  onClose(value: boolean): void
  icon?: React.ElementType
  iconVariant?: 'success' | 'info' | 'danger' | 'warning'
  showHideIcon?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'
  prompt?: boolean
  disallowClose?: boolean
  initialFocus?: React.MutableRefObject<HTMLElement | null>
}

export const Modal = ({
  title,
  children,
  isOpen,
  onClose,
  icon: Icon,
  iconVariant = 'danger',
  showHideIcon = true,
  disallowClose = false,
  size = 'lg',
  prompt = false,
  initialFocus,
}: ModalProps) => {
  const modalSize = useMemo(() => {
    switch (size) {
      case 'xs':
        return 'sm:max-w-xs'
      case 'sm':
        return 'sm:max-w-sm'
      case 'md':
        return 'sm:max-w-md'
      case 'lg':
        return 'sm:max-w-lg'
      case 'xl':
        return 'sm:max-w-xl'
      case '2xl':
        return 'sm:max-w-2xl'
      case '3xl':
        return 'sm:max-w-3xl'
      case '4xl':
        return 'sm:max-w-4xl'
      case '5xl':
        return 'sm:max-w-5xl'
    }
  }, [size])
  const iconBackgoundColor = useMemo(() => {
    switch (iconVariant) {
      case 'success':
        return 'bg-green-500'
      case 'info':
        return 'bg-blue-500'
      case 'danger':
        return 'bg-red-500'
      case 'warning':
        return 'bg-yellow-500'
    }
  }, [iconVariant])

  const [firedOpenedEvent, setFiredOpenedEvent] = useState(false)
  const { fireEvent } = useAnalytics()

  useEffect(() => {
    if (isOpen && !firedOpenedEvent) {
      fireEvent('modal_opened', { modal_title: title })
      setFiredOpenedEvent(true)
    }
  }, [isOpen, firedOpenedEvent, title, fireEvent])

  const _onClose = (value: boolean) => {
    fireEvent('modal_closed', { modal_title: title })
    setFiredOpenedEvent(false)
    onClose(value)
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        className={classNames('relative z-50')}
        onClose={(value) => (disallowClose ? null : _onClose(value))}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <div
            className="fixed inset-0 bg-gray-500/25 dark:bg-black/50 transition-opacity backdrop-filter backdrop-blur"
            aria-hidden="true"
          />
        </Transition.Child>

        {/* Full-screen scrollable container */}
        <div className="fixed inset-0 flex justify-center p-4 overflow-y-auto">
          {/* Container to center the panel */}
          <div
            className={classNames(
              'flex min-h-full mt-auto items-center justify-center w-full',
              modalSize
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="dark:text-white text-left sm:m-0 inline-block align-bottom bg-background rounded-lg shadow-xl transform transition-all sm:my-8 sm:mb-48 sm:align-middle w-full p-6">
                {showHideIcon && (
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet"
                      onClick={() => _onClose(true)}
                      tabIndex={-1}
                    >
                      <span className="sr-only">Close</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {(Icon || title) && (
                  <div className="flex items-center mb-4">
                    {Icon && (
                      <div
                        className={`flex-shrink-0 flex items-center justify-center p-1.5 rounded-lg ${iconBackgoundColor}`}
                      >
                        <Icon className={`h-4 w-4 text-white`} aria-hidden="true" />
                      </div>
                    )}

                    {title && (
                      <div className={`text-left mt-0 flex-1 ${Icon ? 'ml-2' : ''}`}>
                        <Dialog.Title as={Text} type="headline">
                          {title}
                        </Dialog.Title>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-2">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export const ModalButtons = ({ children, prompt = false }) => (
  <div
    className={`mt-6 pb-0 flex space-y-2 space-y-reverse text-center ${
      !prompt ? 'sm:space-y-0 sm:space-x-2 sm:space-x-reverse sm:flex-row-reverse' : ''
    } justify-start flex-col-reverse`}
  >
    {children}
  </div>
)

export interface ConfirmationModalProps {
  title: string
  children: ReactNode
  isOpen: boolean
  /** @deprecated */
  setIsOpen?(value: boolean): void
  onClose(value: boolean): void
  onConfirm(): void
  variant?: 'success' | 'info' | 'danger' | 'warning'
  icon?: (props: React.ComponentProps<'svg'>) => JSX.Element
  confirmButtonLabel: string
  confirming?: boolean
  confirmButtonIcon?: (props: React.ComponentProps<'svg'>) => JSX.Element
  cancelButtonLabel?: string
  disabled?: boolean
}

export const ConfirmationModal = ({
  title,
  isOpen,
  onClose,
  variant = 'danger',
  confirmButtonLabel,
  onConfirm,
  confirming = false,
  confirmButtonIcon: ConfirmButtonIcon,
  disabled = false,
  children,
  icon = ExclamationIcon,
}: ConfirmationModalProps) => {
  const { t } = useTranslation()

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      iconVariant={variant}
      showHideIcon={false}
    >
      <>
        {children}
        <ModalButtons>
          <Button
            variant="primary"
            disabled={disabled}
            label={confirmButtonLabel}
            onClick={onConfirm}
            loading={confirming}
            leadingIcon={ConfirmButtonIcon}
          />
          <Button label={t('modal_button.cancel')} onClick={() => onClose(false)} />
        </ModalButtons>
      </>
    </Modal>
  )
}
